import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { WASTE_CATEGORIES } from '../config/wasteCategories';
import api from '../config/api';
import { toast } from 'react-toastify';
import { FaCamera, FaMapMarkerAlt, FaSpinner, FaTrash, FaImages, FaStar } from 'react-icons/fa';
import './ReportWaste.css';

const MAX_PHOTOS = 2;

const ReportWaste = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    category: '',
    wasteType: '',
    description: ''
  });

  const [foodData, setFoodData] = useState({
    foodType: 'packaged',
    allergenDeclaration: false,
    allergenDetails: '',
    eatBy: '',
    collectionDeadline: '',
    foodDisclaimerAccepted: false
  });

  const [charityData, setCharityData] = useState({
    itemCondition: 0
  });
  
  const [location, setLocation] = useState(null);
  const [locationMethod, setLocationMethod] = useState('gps');
  const [manualAddress, setManualAddress] = useState('');
  const [gettingLocation, setGettingLocation] = useState(true);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    getGPSLocation();
  }, []);

  useEffect(() => {
    if (formData.category && WASTE_CATEGORIES[formData.category]) {
      setAvailableTypes(WASTE_CATEGORIES[formData.category].types);
      setFormData(prev => ({ ...prev, wasteType: '' }));
    }
  }, [formData.category]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLocationMethodChange = (method) => {
    setLocationMethod(method);
    setLocation(null);
    setManualAddress('');
    
    if (method === 'gps') {
      getGPSLocation();
    }
  };

  const getGPSLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setGettingLocation(false);
          toast.success(`Location detected (accuracy: ${position.coords.accuracy.toFixed(0)}m)`);
        },
        (error) => {
          console.error('Location error:', error);
          setGettingLocation(false);
          if (error.code === 1) {
            toast.error('Location permission denied. Please enable location access or use manual entry.');
          } else if (error.code === 2) {
            toast.error('Location unavailable. Please try manual entry.');
          } else {
            toast.error('Location request timed out. Please try again or use manual entry.');
          }
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      setGettingLocation(false);
    }
  };

  const geocodeAddress = async () => {
    if (!manualAddress.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    setGeocodingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(manualAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        setLocation({
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          accuracy: 100,
          address: data[0].display_name
        });
        toast.success('Location found!');
      } else {
        toast.error('Could not find that address. Please try a different one.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      toast.error('Error finding address. Please try again.');
    } finally {
      setGeocodingAddress(false);
    }
  };

  const handleCameraClick = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleGalleryClick = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.click();
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = MAX_PHOTOS - photoFiles.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }
    
    const filesToAdd = files.slice(0, remainingSlots);
    const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
    
    setPhotoFiles(prev => [...prev, ...filesToAdd]);
    setPhotoPreviews(prev => [...prev, ...newPreviews]);
    
    if (files.length > remainingSlots) {
      toast.info(`Only ${remainingSlots} more photo(s) allowed`);
    }
    
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category || !formData.wasteType) {
      toast.error('Please select category and waste type');
      return;
    }

    if (!location) {
      toast.error('Please wait for location or enter address manually');
      return;
    }

    if (photoFiles.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }

    if (formData.category === 'food_donation') {
      if (foodData.foodType === 'home_cooked' && !foodData.allergenDeclaration) {
        toast.error('Allergen declaration is mandatory for home-cooked food');
        return;
      }
      if (foodData.foodType === 'home_cooked' && foodData.allergenDeclaration && !foodData.allergenDetails.trim()) {
        toast.error('Please provide allergen details');
        return;
      }
      if (!foodData.eatBy) {
        toast.error('Please provide an eat-by date/time');
        return;
      }
      if (!foodData.collectionDeadline) {
        toast.error('Please provide a collection deadline');
        return;
      }
      if (!foodData.foodDisclaimerAccepted) {
        toast.error('You must accept the food safety disclaimer');
        return;
      }
    }

    if (formData.category === 'charity_items') {
      if (!charityData.itemCondition || charityData.itemCondition < 1) {
        toast.error('Please rate the item condition (1-5 stars)');
        return;
      }
    }

    try {
      setLoading(true);

      const reportData = {
        category: formData.category,
        wasteType: formData.wasteType,
        description: formData.description,
        evidenceType: 'photo',
        location: location,
        reportedBy: currentUser.displayName || currentUser.email,
        reportedById: currentUser.uid
      };

      if (formData.category === 'food_donation') {
        reportData.is_food_donation = true;
        reportData.food_type = foodData.foodType;
        reportData.allergen_declaration = foodData.allergenDeclaration;
        reportData.allergen_details = foodData.allergenDetails;
        reportData.eat_by = foodData.eatBy;
        reportData.collection_deadline = foodData.collectionDeadline;
        reportData.food_disclaimer_accepted = foodData.foodDisclaimerAccepted;
      }

      if (formData.category === 'charity_items') {
        reportData.is_charity_item = true;
        reportData.item_condition = charityData.itemCondition;
      }

      await api.createWasteReport(reportData, photoFiles[0]);

      toast.success('Waste report submitted successfully!');
      navigate('/map');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="report-waste-page">
      <div className="report-container">
        <h1>Report Waste</h1>
        <p className="report-subtitle">Help keep your community clean by reporting waste</p>

        <form onSubmit={handleSubmit} className="report-form">
          <div className="form-group">
            <label htmlFor="category">Waste Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Category</option>
              {Object.keys(WASTE_CATEGORIES).map((key) => (
                <option key={key} value={key}>
                  {WASTE_CATEGORIES[key].icon} {WASTE_CATEGORIES[key].name}
                </option>
              ))}
            </select>
          </div>

          {formData.category && (
            <div className="form-group">
              <label htmlFor="wasteType">Specific Waste Type *</label>
              <select
                id="wasteType"
                name="wasteType"
                value={formData.wasteType}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Type</option>
                {availableTypes.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.category === 'food_donation' && (
            <div className="conditional-fields food-donation-fields">
              <h3 className="conditional-fields-title">Food Donation Details</h3>
              
              <div className="form-group">
                <label>Food Type *</label>
                <div className="toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${foodData.foodType === 'packaged' ? 'active' : ''}`}
                    onClick={() => setFoodData(prev => ({ ...prev, foodType: 'packaged' }))}
                  >
                    Packaged
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${foodData.foodType === 'home_cooked' ? 'active' : ''}`}
                    onClick={() => setFoodData(prev => ({ ...prev, foodType: 'home_cooked' }))}
                  >
                    Home Cooked
                  </button>
                </div>
              </div>

              {foodData.foodType === 'home_cooked' && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={foodData.allergenDeclaration}
                      onChange={(e) => setFoodData(prev => ({ ...prev, allergenDeclaration: e.target.checked }))}
                    />
                    Allergen Declaration (mandatory for home-cooked) *
                  </label>
                  {foodData.allergenDeclaration && (
                    <textarea
                      placeholder="List all allergens present (e.g., nuts, gluten, dairy, eggs...)"
                      value={foodData.allergenDetails}
                      onChange={(e) => setFoodData(prev => ({ ...prev, allergenDetails: e.target.value }))}
                      rows="3"
                      className="allergen-details-input"
                    />
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="eatBy">Eat By Date/Time *</label>
                <input
                  type="datetime-local"
                  id="eatBy"
                  value={foodData.eatBy}
                  onChange={(e) => setFoodData(prev => ({ ...prev, eatBy: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="collectionDeadline">Collection Deadline *</label>
                <input
                  type="datetime-local"
                  id="collectionDeadline"
                  value={foodData.collectionDeadline}
                  onChange={(e) => setFoodData(prev => ({ ...prev, collectionDeadline: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label disclaimer-label">
                  <input
                    type="checkbox"
                    checked={foodData.foodDisclaimerAccepted}
                    onChange={(e) => setFoodData(prev => ({ ...prev, foodDisclaimerAccepted: e.target.checked }))}
                  />
                  I confirm this food is safe for consumption and I accept responsibility for the accuracy of this listing. *
                </label>
              </div>
            </div>
          )}

          {formData.category === 'charity_items' && (
            <div className="conditional-fields charity-items-fields">
              <h3 className="conditional-fields-title">Charity Item Details</h3>
              
              <div className="form-group">
                <label>Item Condition *</label>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star-btn ${star <= charityData.itemCondition ? 'active' : ''}`}
                      onClick={() => setCharityData(prev => ({ ...prev, itemCondition: star }))}
                    >
                      <FaStar />
                    </button>
                  ))}
                  <span className="star-label">
                    {charityData.itemCondition === 0 && 'Select rating'}
                    {charityData.itemCondition === 1 && 'Poor'}
                    {charityData.itemCondition === 2 && 'Fair'}
                    {charityData.itemCondition === 3 && 'Good'}
                    {charityData.itemCondition === 4 && 'Very Good'}
                    {charityData.itemCondition === 5 && 'Excellent'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Provide additional details about the waste..."
            />
          </div>

          <div className="form-group">
            <label>
              <FaMapMarkerAlt /> Location of Waste *
            </label>
            <select
              className="location-dropdown"
              value={locationMethod}
              onChange={(e) => handleLocationMethodChange(e.target.value)}
            >
              <option value="gps">Live/Current Location (GPS)</option>
              <option value="manual">Postcode/Zipcode/Street</option>
            </select>
            
            {locationMethod === 'gps' && (
              <div className="location-status">
                {gettingLocation ? (
                  <span><FaSpinner className="spinning" /> Detecting your location...</span>
                ) : location ? (
                  <span className="location-success">
                    Location captured (accuracy: {location.accuracy?.toFixed(0)}m)
                  </span>
                ) : (
                  <button type="button" onClick={getGPSLocation} className="retry-location-btn">
                    Retry Location
                  </button>
                )}
              </div>
            )}
            
            {locationMethod === 'manual' && (
              <div className="manual-address-section">
                <input
                  type="text"
                  className="address-input"
                  placeholder="Enter postcode, zipcode, or street address..."
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), geocodeAddress())}
                />
                <button
                  type="button"
                  className="find-location-btn"
                  onClick={geocodeAddress}
                  disabled={geocodingAddress || !manualAddress.trim()}
                >
                  {geocodingAddress ? (
                    <><FaSpinner className="spinning" /> Finding...</>
                  ) : (
                    'Find Location'
                  )}
                </button>
                {location && location.address && (
                  <p className="found-address">Found: {location.address}</p>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Photo Evidence * (max {MAX_PHOTOS})</label>
            
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoChange}
              ref={cameraInputRef}
              style={{ display: 'none' }}
            />
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              ref={galleryInputRef}
              style={{ display: 'none' }}
            />
            
            <div className="photo-buttons">
              <button
                type="button"
                className="take-photo-btn"
                onClick={handleCameraClick}
                disabled={photoFiles.length >= MAX_PHOTOS}
              >
                <FaCamera /> Take Photo
              </button>
              <button
                type="button"
                className="upload-photo-btn"
                onClick={handleGalleryClick}
                disabled={photoFiles.length >= MAX_PHOTOS}
              >
                <FaImages /> Gallery
              </button>
            </div>
            
            {photoPreviews.length > 0 && (
              <div className="photo-previews-grid">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="preview-container">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button 
                      type="button"
                      className="remove-photo-btn"
                      onClick={() => removePhoto(index)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <p className="photo-count">{photoFiles.length}/{MAX_PHOTOS} photos added</p>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || !location || photoFiles.length === 0}
          >
            {loading ? (
              <>
                <FaSpinner className="spinning" /> Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReportWaste;
